<template>
  <UpgradeBanner feature="gestion des employés" />

  <div class="px-6 py-6 space-y-6 min-h-screen select-none text-left transition-colors duration-200 font-sans"
    :class="isDark ? 'bg-zinc-950 text-zinc-400' : 'bg-zinc-50 text-zinc-600'">

    <div class="flex sm:flex-row flex-col justify-between items-start sm:items-center border-b pb-5 gap-4 transition-colors duration-200"
      :class="isDark ? 'border-zinc-900' : 'border-zinc-200'">
      <div class="space-y-0.5">
        <h1 class="text-xl font-semibold tracking-tight transition-colors duration-200" :class="isDark ? 'text-white' : 'text-zinc-900'">
          Registre des collaborateurs
        </h1>
        <p class="text-xs font-medium transition-colors duration-200" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">
          Supervisez votre équipe, suivez leur occupation et gérez leurs accès au planning.
        </p>
      </div>

      <button @click="router.push('/staff/new')" 
        class="sm:w-auto px-5 py-2.5 rounded-xl text-xs font-semibold uppercase tracking-wider transition-all duration-300 shadow-sm cursor-pointer flex items-center gap-2"
        :class="isDark ? 'bg-white hover:bg-zinc-200 text-black' : 'bg-zinc-900 hover:bg-zinc-800 text-white'">
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/></svg>
        Enrôler un collaborateur
      </button>
    </div>

    <div v-if="isLoading" class="flex justify-center items-center py-20">
      <div class="text-xs font-medium animate-pulse" :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">Chargement de l'équipe...</div>
    </div>

    <div v-else class="grid md:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
      <div v-for="member in team" :key="member._id" 
        class="border rounded-2xl p-5 space-y-5 flex flex-col justify-between transition-all duration-300 relative group"
        :class="isDark ? 'bg-zinc-900/20 border-zinc-900 hover:border-zinc-800' : 'bg-white border-zinc-200 shadow-sm hover:border-zinc-300'">

        <div class="space-y-4">
          <div class="flex items-start gap-4">
            <div class="relative">
              <img :src="member.avatar || `https://ui-avatars.com/api/?background=27272a&color=fff&name=${member.prenom}+${member.nom}`"
                alt="Collaborateur" class="w-14 h-14 rounded-full object-cover border transition-all duration-300"
                :class="isDark ? 'border-zinc-800' : 'border-zinc-200'" />
              <span class="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2"
                :class="[member.statut === 'actif' ? 'bg-emerald-500' : 'bg-zinc-400', isDark ? 'border-zinc-950' : 'border-white']"></span>
            </div>

            <div class="space-y-0.5 min-w-0 flex-1 text-left">
              <h2 class="text-sm font-semibold truncate transition-colors" :class="isDark ? 'text-white' : 'text-zinc-900'">
                {{ member.prenom }} {{ member.nom }}
              </h2>
              <p class="text-[10px] font-semibold uppercase tracking-wider" :class="isDark ? 'text-amber-500/80' : 'text-amber-600'">{{ member.specialite || 'Styliste' }}</p>
            </div>
          </div>

          <div class="rounded-xl p-3 grid grid-cols-2 gap-2 text-left border transition-colors" 
            :class="isDark ? 'bg-zinc-950/60 border-zinc-900/60' : 'bg-zinc-50 border-zinc-100'">
            <div class="space-y-0.5">
              <span class="text-[9px] uppercase tracking-wider font-semibold" :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">Téléphone</span>
              <span class="block text-xs font-semibold" :class="isDark ? 'text-zinc-300' : 'text-zinc-700'">{{ member.telephone || 'N/A' }}</span>
            </div>
            <div class="space-y-0.5">
              <span class="text-[9px] uppercase tracking-wider font-semibold" :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">Statut</span>
              <span class="block text-xs font-semibold" :class="member.statut === 'actif' ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-zinc-500' : 'text-zinc-400')">
                {{ member.statut === 'actif' ? 'Actif' : 'Inactif' }}
              </span>
            </div>
          </div>
        </div>

        <div class="pt-4 border-t flex justify-between items-center mt-auto" :class="isDark ? 'border-zinc-900/60' : 'border-zinc-100'">
          <label class="relative inline-flex items-center cursor-pointer select-none">
            <input type="checkbox" :checked="member.statut === 'actif'" @change="toggleStatus(member)" class="sr-only peer" />
            <div class="w-8 h-4 rounded-full transition-colors" :class="member.statut === 'actif' ? 'bg-emerald-600' : (isDark ? 'bg-zinc-800' : 'bg-zinc-200')">
              <div class="w-3 h-3 m-0.5 rounded-full bg-white transition-transform" :class="member.statut === 'actif' ? 'translate-x-4' : ''"></div>
            </div>
          </label>

          <div class="flex items-center gap-1.5 transition-opacity">
            <button @click="router.push(`/staff/${member._id}`)" title="Voir profil" class="p-2 rounded-xl transition-colors" :class="isDark ? 'bg-zinc-900 hover:bg-zinc-800 text-zinc-400' : 'bg-zinc-100 hover:bg-zinc-200 text-zinc-500'">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"/><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
            </button>
            <button @click="editStaff(member)" title="Éditer" class="p-2 rounded-xl transition-colors" :class="isDark ? 'bg-zinc-900 hover:bg-zinc-800 text-zinc-400' : 'bg-zinc-100 hover:bg-zinc-200 text-zinc-500'">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" /></svg>
            </button>
            <button @click="deleteStaff(member._id)" title="Retirer" class="p-2 rounded-xl transition-colors" :class="isDark ? 'bg-zinc-900 hover:bg-rose-950/60 text-zinc-600 hover:text-rose-400' : 'bg-zinc-100 hover:bg-rose-50 text-zinc-400 hover:text-rose-600'">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.12-2.212a37.96 37.96 0 00-3.435-.116c-1.173 0-2.31.04-3.435.116-1.21.048-2.12 1.032-2.12 2.212v.916m7.5 0H7.5" /></svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!isLoading && team.length === 0" class="text-center py-20">
      <p class="text-xs font-medium" :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">Aucun membre de l'équipe trouvé</p>
      <button @click="router.push('/staff/new')" class="mt-4 px-4 py-2 rounded-xl text-xs font-semibold uppercase tracking-wider transition-colors cursor-pointer"
        :class="isDark ? 'bg-white hover:bg-zinc-200 text-black' : 'bg-zinc-900 hover:bg-zinc-800 text-white'">
        Ajouter le premier collaborateur
      </button>
    </div>
  </div>
</template>
<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import api from '@/api'
import UpgradeBanner from '@/components/UpgradeBanner.vue'
import { useTheme } from '@/composables/useTheme';

// Variable globale injectée ou reçue par props pour basculer de thème
const { isDark } = useTheme();
const router = useRouter()
const isLoading = ref(true)
const team = ref([])

onMounted(async () => {
  await fetchTeam()
})

const fetchTeam = async () => {
  try {
    isLoading.value = true
    const response = await api.staff.getAllStaff()
    team.value = response.data?.administrateurs || []
  } catch (error) {
    console.error('Erreur lors de la récupération de l\'équipe:', error)
    toast.error('Erreur lors du chargement de l\'équipe', {
      position: 'top-right',
      autoClose: 3000
    })
  } finally {
    isLoading.value = false
  }
}

const toggleStatus = async (member) => {
  try {
    const newStatus = member.statut === 'actif' ? 'inactif' : 'actif'

    await api.staff.updateStaff(member._id, { statut: newStatus })

    member.statut = newStatus

    toast.success(`Statut mis à jour`, {
      position: 'top-right',
      autoClose: 2000
    })
  } catch (error) {
    console.error('Erreur lors de la mise à jour du statut:', error)
    toast.error('Erreur lors de la mise à jour', {
      position: 'top-right',
      autoClose: 3000
    })
  }
}

const editStaff = (member) => {
  router.push(`/staff/new?edit=${member._id}`)
}

const deleteStaff = async (id) => {
  if (!confirm('Êtes-vous sûr de vouloir supprimer ce membre de l\'équipe ?')) {
    return
  }

  try {
    await api.staff.deleteStaff(id)

    toast.success('Membre supprimé avec succès', {
      position: 'top-right',
      autoClose: 2000
    })

    await fetchTeam()
  } catch (error) {
    console.error('Erreur lors de la suppression:', error)
    toast.error('Erreur lors de la suppression', {
      position: 'top-right',
      autoClose: 3000
    })
  }
}
</script>
